import React, { useState, useEffect, useRef } from 'react';

const QUESTIONS = [
  "Should I stay in this relationship?",
  "Should I raise the next round now?",
  "Should I leave this job?",
  "Should I move to a new city?",
  "Should I go all in on this business?",
  "Should I say yes to this?"
];

// Timing constants (ms)
const T_BRANCHES_IN = 900;
const T_ENDPOINTS_IN = 1600;
const T_FADE_OUT = 5800;
const T_NEXT_IN = 6600;

const AnimatedQuestionTree = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState('question'); // question, branches, endpoints, visible, fadeout, hidden
  const [leftBranchProgress, setLeftBranchProgress] = useState(0);
  const [rightBranchProgress, setRightBranchProgress] = useState(0);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Start the animation cycle
    const runCycle = () => {
      // Phase 1: Question fades in
      setPhase('question');
      setLeftBranchProgress(0);
      setRightBranchProgress(0);

      // Phase 2: Branches draw
      timeoutRef.current = setTimeout(() => {
        setPhase('branches');
        // Animate left branch
        setLeftBranchProgress(1);
        // Animate right branch with delay
        setTimeout(() => {
          setRightBranchProgress(1);
        }, 120);
      }, T_BRANCHES_IN);

      // Phase 3: Endpoints appear
      setTimeout(() => {
        setPhase('endpoints');
      }, T_ENDPOINTS_IN);

      // Phase 4: Fade out
      setTimeout(() => {
        setPhase('fadeout');
      }, T_FADE_OUT);

      // Phase 5: Next question
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % QUESTIONS.length);
      }, T_NEXT_IN);
    };

    runCycle();

    // Set up interval for continuous cycling
    const interval = setInterval(runCycle, T_NEXT_IN);

    return () => {
      clearInterval(interval);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [currentIndex]);

  const showQuestion = phase !== 'hidden';
  const showBranches = phase === 'branches' || phase === 'endpoints' || phase === 'fadeout';
  const showEndpoints = phase === 'endpoints' || phase === 'fadeout';
  const isFading = phase === 'fadeout';

  return (
    <div className="aqt-container" data-testid="animated-question-tree">
      <div className={`aqt-content ${isFading ? 'aqt-fadeout' : ''}`}>
        {/* Question Card */}
        <div className={`aqt-question-card ${showQuestion ? 'aqt-visible' : ''}`}>
          {QUESTIONS[currentIndex]}
        </div>

        {/* Branch Lines SVG */}
        <svg 
          className="aqt-branches" 
          viewBox="0 0 380 90" 
          fill="none"
        >
          {/* Left branch */}
          <path
            d="M190 0 L190 30 L80 80"
            stroke="#111111"
            strokeWidth="1.5"
            fill="none"
            className="aqt-branch-left"
            style={{
              strokeDasharray: 200,
              strokeDashoffset: showBranches ? 0 : 200,
            }}
          />
          {/* Right branch */}
          <path
            d="M190 0 L190 30 L300 80"
            stroke="#111111"
            strokeWidth="1.5"
            fill="none"
            className="aqt-branch-right"
            style={{
              strokeDasharray: 200,
              strokeDashoffset: showBranches ? 0 : 200,
              transitionDelay: '120ms',
            }}
          />
        </svg>

        {/* Endpoint Nodes */}
        <div className="aqt-endpoints">
          <div className={`aqt-endpoint aqt-endpoint-yes ${showEndpoints ? 'aqt-visible' : ''}`}>
            <div className="aqt-endpoint-node aqt-yes">
              <span className="aqt-diamond">◆</span> YES
            </div>
            <span className="aqt-endpoint-label">this path</span>
          </div>
          <div className={`aqt-endpoint aqt-endpoint-no ${showEndpoints ? 'aqt-visible' : ''}`} style={{ transitionDelay: '80ms' }}>
            <div className="aqt-endpoint-node aqt-no">
              NO
            </div>
            <span className="aqt-endpoint-label">or this one</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimatedQuestionTree;
